import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Tag,
  Search,
  AlertCircle
} from 'lucide-react';
import { categoriesApi, domainsApi } from '../services/api';
import { Category, CategoryCreate, CategoryUpdate, Domain } from '../types';

interface TestTypesProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const TestTypes: React.FC<TestTypesProps> = ({ addNotification }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<number | ''>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<CategoryCreate>({
    name: '',
    domain_id: 0,
    description: '',
    purpose: '',
    status: 'approved'
  });

  useEffect(() => {
    loadDomains();
    loadCategories();
  }, []);

  const loadDomains = async () => {
    try {
      const response = await domainsApi.list();
      setDomains(response.domains);
      // Set first domain as default if available
      if (response.domains.length > 0 && formData.domain_id === 0) {
        setFormData(prev => ({ ...prev, domain_id: response.domains[0].id }));
      }
    } catch (error) {
      addNotification('Failed to load domains', 'error');
      console.error('Error loading domains:', error);
    }
  };

  const loadCategories = async () => {
    try {
      setLoading(true);
      const params: any = { size: 100 }; // Request up to 100 categories
      if (selectedDomain) params.domain_id = selectedDomain;
      if (searchTerm) params.search = searchTerm;
      
      const response = await categoriesApi.list(params);
      setCategories(response.categories);
    } catch (error) {
      addNotification('Failed to load categories', 'error');
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    try {
      if (!formData.name.trim()) {
        addNotification('Category name is required', 'error');
        return;
      }

      if (!formData.domain_id) {
        addNotification('Please select a domain', 'error');
        return;
      }

      await categoriesApi.create(formData);
      addNotification('Category created successfully', 'success');
      setShowCreateModal(false);
      setFormData({ 
        name: '', 
        domain_id: domains.length > 0 ? domains[0].id : 0, 
        description: '', 
        purpose: '',
        status: 'approved'
      });
      loadCategories();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to create category';
      addNotification(message, 'error');
      console.error('Error creating category:', error);
    }
  };

  const handleEditCategory = async () => {
    if (!editingCategory) return;

    try {
      if (!formData.name.trim()) {
        addNotification('Category name is required', 'error');
        return;
      }

      const updateData: CategoryUpdate = {
        name: formData.name,
        domain_id: formData.domain_id,
        description: formData.description || undefined,
        purpose: formData.purpose || undefined,
        status: formData.status || undefined,
      };

      await categoriesApi.update(editingCategory.id, updateData);
      addNotification('Category updated successfully', 'success');
      setShowEditModal(false);
      setEditingCategory(null);
      loadCategories();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update category';
      addNotification(message, 'error');
      console.error('Error updating category:', error);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      await categoriesApi.delete(id);
      addNotification('Category deleted successfully', 'success');
      loadCategories();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to delete category';
      addNotification(message, 'error');
      console.error('Error deleting category:', error);
    }
  };

  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      domain_id: category.domain_id,
      description: category.description || '',
      purpose: category.purpose || '',
      status: category.status || 'pending'
    });
    setShowEditModal(true);
  };

  const getDomainColor = (domainName: string) => {
    switch (domainName?.toLowerCase()) {
      case 'knowledge base':
        return 'bg-blue-900/50 text-blue-200';
      case 'rag pipeline':
        return 'bg-purple-900/50 text-purple-200';
      case 'agent system':
        return 'bg-green-900/50 text-green-200';
      default:
        return 'bg-slate-700 text-slate-300';
    }
  };

  const getDomainName = (domainId: number): string => {
    const domain = domains.find(d => d.id === domainId);
    return domain?.name || 'Unknown';
  };

  const filteredCategories = categories.filter(cat => {
    if (selectedDomain && cat.domain_id !== selectedDomain) return false;
    if (searchTerm && !cat.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Categories</h1>
            <p className="text-slate-300 mt-2">Manage test categories organized by domains (Domain → Category → Technique)</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => {
                setFormData({
                  name: '',
                  domain_id: domains.length > 0 ? domains[0].id : 0,
                  description: '',
                  purpose: '',
                  status: 'pending'
                });
                setShowCreateModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Category
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-200 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name..."
                className="w-full pl-10 pr-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-1">Filter by Domain</label>
            <select
              value={selectedDomain}
              onChange={(e) => setSelectedDomain(e.target.value ? Number(e.target.value) : '')}
              className="px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
            >
              <option value="">All Domains</option>
              {domains.map((domain) => (
                <option key={domain.id} value={domain.id}>{domain.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Categories Table */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
        <div className="px-6 py-4 border-b border-slate-700">
          <h3 className="text-lg font-medium text-slate-100">
            Categories ({filteredCategories.length})
          </h3>
        </div>
        
        {filteredCategories.length === 0 ? (
          <div className="text-center py-12">
            <Tag className="h-12 w-12 mx-auto mb-3 text-slate-500" />
            <p className="text-slate-400 text-lg">No categories found</p>
            <p className="text-slate-500 text-sm">
              {categories.length === 0
                ? 'Create your first category to get started'
                : 'Try adjusting your filters'
              }
            </p>
            {categories.length === 0 && (
              <div className="mt-4">
                <button
                  onClick={() => {
                    setFormData({
                      name: '',
                      domain_id: domains.length > 0 ? domains[0].id : 0,
                      description: '',
                      purpose: '',
                      status: 'pending'
                    });
                    setShowCreateModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Category
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Domain (Layer)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Category Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Purpose
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Techniques
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-slate-800 divide-y divide-slate-700">
                {filteredCategories.map((category) => {
                  return (
                    <tr key={category.id} className="hover:bg-slate-700/30">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getDomainColor(category.domain_name || '')}`}>
                            <Tag className="h-3 w-3 mr-1" />
                            {category.domain_name}
                          </span>
                          {category.domain_layer && (
                            <span className="text-xs text-slate-400 italic">
                              {category.domain_layer}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-100">{category.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-100 max-w-md">
                          {category.purpose ? (
                            <div className="break-words whitespace-normal line-clamp-2" title={category.purpose}>
                              {category.purpose}
                            </div>
                          ) : (
                            <span className="text-slate-500 italic">No purpose defined</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-indigo-600/30 text-indigo-200">
                          {category.techniques_count || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                          category.status === 'approved' ? 'bg-green-900/50 text-green-200' :
                          category.status === 'pending' ? 'bg-amber-900/50 text-amber-200' :
                          category.status === 'declined' ? 'bg-red-900/50 text-red-200' :
                          'bg-slate-700 text-slate-300'
                        }`}>
                          {category.status.charAt(0).toUpperCase() + category.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(category)}
                            className="inline-flex items-center px-3 py-1 border border-slate-600 text-xs font-medium rounded text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </button>
                          
                          <button
                            onClick={() => handleDeleteCategory(category.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-200 bg-red-900/50 hover:bg-red-800/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Category Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Create New Category</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Domain *
                  </label>
                  <select
                    value={formData.domain_id}
                    onChange={(e) => setFormData({ ...formData, domain_id: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value={0}>Select a domain...</option>
                    {domains.map((domain) => (
                      <option key={domain.id} value={domain.id}>{domain.name}</option>
                    ))}
                  </select>
                  {domains.length === 0 && (
                    <div className="mt-2 flex items-center text-sm text-amber-400">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      No domains available. Create a domain first.
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Content Retrieval / Understanding"
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Purpose
                  </label>
                  <textarea
                    value={formData.purpose}
                    onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                    placeholder="What is the purpose of this category?"
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Additional details..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleCreateCategory}
                  disabled={domains.length === 0}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    domains.length === 0 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                  }`}
                >
                  Create
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditModal && editingCategory && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Edit Category</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Domain *
                  </label>
                  <select
                    value={formData.domain_id}
                    onChange={(e) => setFormData({ ...formData, domain_id: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    {domains.map((domain) => (
                      <option key={domain.id} value={domain.id}>{domain.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Purpose
                  </label>
                  <textarea
                    value={formData.purpose}
                    onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-slate-800 text-slate-100 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="declined">Declined</option>
                  </select>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleEditCategory}
                  className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingCategory(null);
                  }}
                  className="flex-1 px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestTypes;
